import React, { useLayoutEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { TriangleAlert, Wrench, Info } from "lucide-react";
import { ContentBlock } from "../../types/courseBlocks";
import { HeadingEntry, slugify } from "../../utils/extractHeadings";
import YouTubeFacade from "./YouTubeFacade";
import ImageSlideDeck from "./ImageSlideDeck";
import CanvasSandbox from "./CanvasSandbox";
import LessonFlashcards from "./LessonFlashcards";
import LessonMindmap from "./LessonMindmap";
import LessonQuiz from "./LessonQuiz";

interface BlockRendererProps {
  blocks: ContentBlock[];
  scopeId: string;
  /** Fires after the headings actually rendered are known — lets a caller build a matching table of contents. */
  onHeadingsChange?: (headings: HeadingEntry[]) => void;
}

const calloutIcon = {
  warning: <TriangleAlert aria-hidden="true" size={22} />,
  production: <Wrench aria-hidden="true" size={22} />,
  info: <Info aria-hidden="true" size={22} />,
};

const BlockRenderer: React.FC<BlockRendererProps> = ({ blocks, scopeId, onHeadingsChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Assigning heading ids as a render-time side effect (mutating a shared
  // counter while React calls each heading's render function) is impure and
  // breaks under React 18 StrictMode, which deliberately double-invokes
  // render to catch exactly that — it was producing duplicate entries. This
  // reads the real, already-rendered DOM instead, after the fact, which is
  // idempotent no matter how many times it runs.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const seen = new Map<string, number>();
    const headings: HeadingEntry[] = [];

    container.querySelectorAll<HTMLElement>("h1, h2, h3, h4").forEach((el) => {
      const text = (el.textContent || "").trim();
      const base = slugify(text) || "section";
      const count = seen.get(base) || 0;
      seen.set(base, count + 1);
      const id = count === 0 ? base : `${base}-${count}`;
      el.id = id;
      el.tabIndex = -1;
      headings.push({ id, text, level: Number(el.tagName.slice(1)) });
    });

    onHeadingsChange?.(headings);
    // onHeadingsChange is expected to be stable-ish (a setState function); we
    // intentionally key this only on blocks so it reruns when content changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]);

  return (
    <div ref={containerRef}>
      {blocks.map((block) => {
        switch (block.type) {
          case "text":
            return (
              <div key={block.id} className="lesson-block lesson-block--text">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {block.markdown}
                </ReactMarkdown>
              </div>
            );
          case "code":
            return (
              <div key={block.id} className="lesson-code">
                <div>
                  <span>{block.language || "text"}</span>
                  {block.filename && <span>{block.filename}</span>}
                </div>
                <pre tabIndex={0}>
                  <code>{block.code}</code>
                </pre>
              </div>
            );
          case "canvas2d":
            return <CanvasSandbox key={block.id} code={block.code} />;
          case "image":
            return (
              <figure key={block.id} className="lesson-hero-image">
                <img src={block.url} alt={block.alt || ""} />
                {(block.caption || block.isPlaceholder) && (
                  <figcaption>
                    {block.caption}
                    {block.isPlaceholder && " (Placeholder image)"}
                  </figcaption>
                )}
              </figure>
            );
          case "slides":
            return <ImageSlideDeck key={block.id} slides={block.slides} />;
          case "flashcards":
            return <LessonFlashcards key={block.id} cards={block.cards} scopeId={`${scopeId}:${block.id}`} />;
          case "mindmap":
            return <LessonMindmap key={block.id} centralLabel={block.centralLabel} nodes={block.nodes} />;
          case "video":
            return block.youtubeVideoId ? (
              <YouTubeFacade
                key={block.id}
                videoId={block.youtubeVideoId}
                title={block.title}
                isIllustrative={block.isIllustrative}
              />
            ) : block.videoUrl ? (
              <figure key={block.id} className="lesson-video">
                <video controls src={block.videoUrl} />
                <p className="lesson-video__fallback">{block.title}</p>
              </figure>
            ) : null;
          case "quiz":
            return <LessonQuiz key={block.id} questions={block.questions} scopeId={`${scopeId}:${block.id}`} />;
          case "callout":
            return (
              <div key={block.id} className={`lesson-callout lesson-callout--${block.variant}`}>
                {calloutIcon[block.variant]}
                <div>
                  {block.heading && <h3>{block.heading}</h3>}
                  <p>{block.text}</p>
                </div>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default BlockRenderer;
